import type { JSX } from "react";
// Typessrc/media
import { IconGridItem } from "../types/IconGrid.interface";
import { Species } from "@spotpetinsurance/spot-types/entities/Species";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import Strings from "@/shared/utils/Strings.constants";

// Icons
import BillIcon from "@/shared/media/icons/policy/Bill.svg";
import CatXrayIcon from "@/shared/media/icons/policy/Cat-xray.svg";
import DogXrayIcon from "@/shared/media/icons/policy/Dog-xray.svg";
import HospitalIcon from "@/shared/media/icons/policy/Hospital.svg";
import HipIcon from "@/shared/media/icons/policy/Hips.svg";
import BrokenBoneIcon from "@/shared/media/icons/policy/Bone-Broken.svg";
import PillBottleIcon from "@/shared/media/icons/policy/Pill-Bottle.svg";
import AlternativeIcon from "@/shared/media/icons/policy/Alternative-Therapy.svg";
import BandaidsIcon from "@/shared/media/icons/policy/Bandaids.svg";
import BoneBrokenIcon from "@/shared/media/icons/policy/Bone-Broken.svg";
import CancerIcon from "@/shared/media/icons/policy/Cancer.svg";
import ClipboardMagnifyingGlassIcon from "@/shared/media/icons/policy/ClipboardMagnifyingGlass.svg";
import DentalDiseaseIcon from "@/shared/media/icons/policy/Dental-Disease.svg";
import DocumentIcon from "@/shared/media/icons/policy/document.svg";
import DocumentIconRebrand from "@/shared/media/icons/policy/Document-rebrand.svg";
import DogSkinMagnifyIcon from "@/shared/media/icons/policy/Dog-Skin-Magnify.svg";
import CatSkinMagnifyIcon from "@/shared/media/icons/policy/Cat-Skin-Magnify.svg";
import HipsIcon from "@/shared/media/icons/policy/Hips.svg";
import IVIcon from "@/shared/media/icons/policy/IV.svg";
import MicrochipDogIcon from "@/shared/media/icons/policy/Microchip-Dog.svg";
import MicrochipCatIcon from "@/shared/media/icons/policy/Microchip-Cat.svg";
import PawHeartBeatIcon from "@/shared/media/icons/policy/Paw-Heartbeat.svg";
import ParasiteIcon from "@/shared/media/icons/policy/Parasite.svg";
import PoisonIcon from "@/shared/media/icons/policy/Poison.svg";
import ShotSyringeIcon from "@/shared/media/icons/policy/Shot-Syringe.svg";
import ShotVaccineIcon from "@/shared/media/icons/policy/Shot-Vaccine.svg";
import ShotVaccineIconRebrand from "@/shared/media/icons/policy/Shot-Vaccine-rebrand.svg";
import StomachIcon from "@/shared/media/icons/policy/Stomach.svg";
import SurgeryIcon from "@/shared/media/icons/policy/Surgery.svg";
import SurgeryIconRebrand from "@/shared/media/icons/policy/Surgery-rebrand.svg";
import TapewormIcon from "@/shared/media/icons/policy/Tapeworm.svg";
import BrokenToothIcon from "@/shared/media/icons/policy/BrokenTooth.svg";
import TelehealthIcon from "@/shared/media/icons/policy/Telehealth.svg";
import WarningIcon from "@/shared/media/icons/policy/Warning.svg";
import ToothBrushIcon from "@/shared/media/icons/policy/Tooth-Brush.svg";
import ToothBrushIconRebrand from "@/shared/media/icons/policy/Tooth-Brush-rebrand.svg";
import CalendarHeartIcon from "@/shared/media/icons/policy/Calendar-Heart.svg";
import DiagnosticsIcon from "@/shared/media/icons/policy/ClipboardMagnifyingGlass.svg";
import HealthCrossPawIcon from "@/shared/media/icons/policy/Health-Cross-Paw.svg";
import HealthCrossPawIconRebrand from "@/shared/media/icons/policy/Health-Cross-Paw-rebrand.svg";
import LickingFurPulling from "@/shared/media/icons/policy/Licking-Fur-Pulling.svg";
import MagnifyingGlassIcon from "@/shared/media/icons/policy/Magnifying-Glass.svg";
import MagnifyingGlassIconRebrand from "@/shared/media/icons/policy/Magnifying-Glass-rebrand.svg";
import { Pet, Quote } from "../types/Quote.interface";
import { SectionGridItem } from "../components/PreventiveCards/SectionGrid";
import { BREED_PREVENTIVE_METADATA, BREED_PREVENTIVE_RULES } from "../PreventiveBreedConfig";

const preventiveIconSize = "size-7";
const categorizedIconSize = "size-8 xs:size-10 sm:size-8 md:size-10 lg:size-8 xl:size-10";

type BenefitLevel = "gold" | "platinum";
interface PreventiveBenefit {
    text: string;
    amountGold: number;
    amountPlatinum: number;
    icon: JSX.Element;
    level: BenefitLevel[];
    section?: BenefitSectionType;
}

interface PreventiveBenefitsParams {
    petAge: number;
    quote: Quote;
    activePetIndex?: number;
    isApplyAllHidden?: boolean;
    applyToAllPets?: boolean;
    showRebrandIcon?: boolean;
    applyBreedRecs?: boolean;
}

export type BenefitSectionType = "procedures" | "tests_exams" | "routine_treatments" | "vaccines_titers" | "documentation";

export interface BenefitSection {
    id: BenefitSectionType;
    title: string;
    icon: JSX.Element;
}

interface GetBenefitsParams {
    showRebrandIcon: boolean;
}

const getBenefitsSections = (props: GetBenefitsParams) => {
    const { showRebrandIcon } = props;

    const sections: BenefitSection[] = [
        {
            id: "procedures",
            title: Strings.PREVENTIVE_CATEGORIES.PROCEDURES,
            icon: showRebrandIcon ? <ToothBrushIconRebrand className={categorizedIconSize} /> : <ToothBrushIcon className={categorizedIconSize} />
        },
        {
            id: "routine_treatments",
            title: Strings.PREVENTIVE_CATEGORIES.TREATMENTS,
            icon: showRebrandIcon ? <HealthCrossPawIconRebrand className={categorizedIconSize} /> : <HealthCrossPawIcon className={categorizedIconSize} />
        },
        {
            id: "tests_exams",
            title: Strings.PREVENTIVE_CATEGORIES.TESTS,
            icon: showRebrandIcon ? <MagnifyingGlassIconRebrand className={categorizedIconSize} /> : <MagnifyingGlassIcon className={categorizedIconSize} />
        },

        {
            id: "vaccines_titers",
            title: Strings.PREVENTIVE_CATEGORIES.VACCINES,
            icon: showRebrandIcon ? <ShotVaccineIconRebrand className={categorizedIconSize} /> : <ShotVaccineIcon className={categorizedIconSize} />
        },
        {
            id: "documentation",
            title: Strings.PREVENTIVE_CATEGORIES.DOCUMENTATION,
            icon: showRebrandIcon ? <DocumentIconRebrand className={categorizedIconSize} /> : <DocumentIcon className={categorizedIconSize} />
        }
    ];

    return sections;
};
export class IconUtils {
    static getAccidentIcons = (species: Species, underwriter: PetUnderwriterType, iconSize = "size-16"): IconGridItem[] => {
        switch (underwriter) {
            case "ptz-us":
                return [
                    {
                        icon: <BillIcon className={iconSize} />,
                        text: `Vet Exam Fees\n& Lab Tests`
                    },
                    {
                        icon: species === Species.Dog ? <DogXrayIcon className={iconSize} /> : <CatXrayIcon className={iconSize} />,
                        text: `MRI or CT Scans\n& X-rays`
                    },
                    {
                        icon: <HospitalIcon className={iconSize} />,
                        text: `Surgery &\nHospitalization`
                    },
                    {
                        icon: <IVIcon className={iconSize} />,
                        text: `IV Fluids &\nMedications`
                    },
                    {
                        icon: <BandaidsIcon className={iconSize} />,
                        text: `Medical Supplies\n(e.g. Bandages & more)`
                    },
                    {
                        icon: <PoisonIcon className={iconSize} />,
                        text: `Poison Control\nConsultation Fees`
                    },
                    {
                        icon: <PillBottleIcon className={iconSize} />,
                        text: `Prescription\nMedications`
                    },
                    {
                        icon: <BrokenToothIcon className={iconSize} />,
                        text: `Broken\nTeeth`
                    },
                    {
                        icon: <ShotSyringeIcon className={iconSize} />,
                        text: `Advanced\nCare`
                    },
                    {
                        icon: <AlternativeIcon className={iconSize} />,
                        text: `Alternative\nTreatments`
                    }
                ];

            default:
                return [];
        }
    };

    static getAccidentIllnessIcons(species: Species, underwriter: PetUnderwriterType, iconSize = "size-16"): IconGridItem[] {
        switch (underwriter) {
            case "ptz-ca":
                return [
                    {
                        icon: <BillIcon className={iconSize} />,
                        text: `Vet Exam Fees`
                    },
                    {
                        icon: species === Species.Dog ? <DogXrayIcon className={iconSize} /> : <CatXrayIcon className={iconSize} />,
                        text: `X-ray & Tests`
                    },
                    {
                        icon: <HospitalIcon className={iconSize} />,
                        text: `Emergencies &\n Hospitalizations`
                    },
                    {
                        icon: species === Species.Dog ? <DogSkinMagnifyIcon className={iconSize} /> : <CatSkinMagnifyIcon className={iconSize} />,
                        text: `Skin, Eye, & Ear\nInfections`
                    },
                    {
                        icon: <PawHeartBeatIcon className={iconSize} />,
                        text: `Hereditary Conditions`
                    },
                    {
                        icon: <StomachIcon className={iconSize} />,
                        text: `Digestive Illnesses`
                    },
                    {
                        icon: <HipIcon className={iconSize} />,
                        text: `Hip Dysplasia`
                    },
                    {
                        icon: <CancerIcon className={iconSize} />,
                        text: `Cancer & Growths`
                    },
                    {
                        icon: <ParasiteIcon className={iconSize} />,
                        text: `Diseases: Infectious\n & from Parasites`
                    },
                    {
                        icon: <BrokenBoneIcon className={iconSize} />,
                        text: `Orthopedic`
                    },
                    {
                        icon: <WarningIcon className={iconSize} />,
                        text: `Swallowed Objects\n& Toxins`
                    },
                    {
                        icon: <ClipboardMagnifyingGlassIcon className={iconSize} />,
                        text: `Allergies &\nTreatments`
                    },

                    {
                        icon: <PillBottleIcon className={iconSize} />,
                        text: `Prescription Medications`
                    },
                    {
                        icon: <SurgeryIcon className={iconSize} />,
                        text: `Surgery &\nSpecialized Care`
                    },
                    {
                        icon: <ShotVaccineIcon className={iconSize} />,
                        text: `Advanced Care`
                    },

                    {
                        icon: <AlternativeIcon className={iconSize} />,
                        text: `Alternative Therapies\n (e.g. Acupunture)`
                    }
                ];

            case "ptz-us":
                return [
                    {
                        icon: <BillIcon className={iconSize} />,
                        text: `Vet Exam Fees\n& Lab Tests`
                    },
                    {
                        icon: <PillBottleIcon className={iconSize} />,
                        text: `Prescription\nMedications & Food`
                    },
                    {
                        icon: species === Species.Dog ? <DogXrayIcon className={iconSize} /> : <CatXrayIcon className={iconSize} />,
                        text: `X-rays, MRIs,\n& CT Scans`
                    },
                    {
                        icon: species === Species.Dog ? <DogSkinMagnifyIcon className={iconSize} /> : <CatSkinMagnifyIcon className={iconSize} />,
                        text: `Skin, Eye, & Ear\nInfections`
                    },
                    {
                        icon: <CancerIcon className={iconSize} />,
                        text: `Cancer &\nChemotherapy`
                    },
                    {
                        icon: <SurgeryIcon className={iconSize} />,
                        text: `Surgery &\nSpecialized Care`
                    },
                    {
                        icon: <PawHeartBeatIcon className={iconSize} />,
                        text: `Congenital &\n Heart Conditions`
                    },
                    {
                        icon: <HospitalIcon className={iconSize} />,
                        text: `Emergencies &\nHospitalizations`
                    },
                    {
                        icon: species === Species.Dog ? <MicrochipDogIcon className={iconSize} /> : <MicrochipCatIcon className={iconSize} />,
                        text: `Microchip\nImplantation`
                    },
                    {
                        icon: <LickingFurPulling className={iconSize} />,
                        text: `Excessive Licking\n& Fur Pulling`
                    },
                    {
                        icon: <DentalDiseaseIcon className={iconSize} />,
                        text: `Dental Illnesses\n& Gum Disease`
                    },
                    {
                        icon: <StomachIcon className={iconSize} />,
                        text: `Digestive Illnesses\n& Diarrhea`
                    },
                    {
                        icon: <HipsIcon className={iconSize} />,
                        text: `Arthritis &\nHip Dysplasia`
                    },
                    {
                        icon: <ParasiteIcon className={iconSize} />,
                        text: `Diseases: Infectious\n & from Parasites`
                    },
                    {
                        icon: <BoneBrokenIcon className={iconSize} />,
                        text: `Orthopedic\nInjuries`
                    },
                    {
                        icon: <WarningIcon className={iconSize} />,
                        text: `Swallowed Objects\n& Poisons`
                    },
                    {
                        icon: <ClipboardMagnifyingGlassIcon className={iconSize} />,
                        text: `Allergies &\nTreatments`
                    },
                    {
                        icon: <ShotVaccineIcon className={iconSize} />,
                        text: `Urinary Tract\nInfections`
                    },
                    {
                        icon: <TelehealthIcon className={iconSize} />,
                        text: `24/7 Virtual\nVet Visits`
                    },
                    {
                        icon: <AlternativeIcon className={iconSize} />,
                        text: `End of Life Expenses\n(e.g. Euthanasia)`
                    }
                ];

            default:
                return [];
        }
    }

    static PREVENTIVE_BENEFITS_PTZ_CA: PreventiveBenefit[] = [
        {
            text: "Dental Cleaning",
            section: "procedures",
            icon: <ToothBrushIcon className={preventiveIconSize} />,
            level: ["platinum"],
            amountGold: 0,
            amountPlatinum: 125
        },
        {
            text: "Spaying/Neutering and/or Wellness or Pre-Anesthetic Blood Tests",
            section: "procedures",
            amountGold: 90,
            amountPlatinum: 115,
            icon: <SurgeryIcon className={preventiveIconSize} />,
            level: ["gold", "platinum"]
        },
        {
            text: "Wellness Exam",
            section: "tests_exams",
            amountGold: 60,
            amountPlatinum: 80,
            icon: <CalendarHeartIcon className={preventiveIconSize} />,
            level: ["gold", "platinum"]
        },
        {
            text: "Vaccinations and titers for dogs (DHLPP) and cats (FVRCP)",
            section: "vaccines_titers",
            amountGold: 20,
            amountPlatinum: 20,
            icon: <ShotVaccineIcon className={preventiveIconSize} />,
            level: ["gold", "platinum"]
        },
        {
            text: "Heartworm or FeLV Screening",
            section: "tests_exams",
            amountGold: 25,
            amountPlatinum: 25,
            icon: <ClipboardMagnifyingGlassIcon className={preventiveIconSize} />,
            level: ["gold", "platinum"]
        },
        {
            text: "Rabies, Canine Lyme Disease, and/or Feline FIP Vaccine/Titer",
            section: "vaccines_titers",
            amountGold: 20,
            amountPlatinum: 20,
            icon: <ShotVaccineIcon className={preventiveIconSize} />,
            level: ["gold", "platinum"]
        },
        {
            text: "Fecal Screen",
            section: "tests_exams",
            amountGold: 15,
            amountPlatinum: 20,
            icon: <ClipboardMagnifyingGlassIcon className={preventiveIconSize} />,
            level: ["gold", "platinum"]
        },
        {
            text: "Heartworm/Flea Prevention",
            section: "routine_treatments",
            amountGold: 0,
            amountPlatinum: 75,
            icon: <HealthCrossPawIcon className={preventiveIconSize} />,
            level: ["platinum"]
        },
        {
            text: "Canine Bordetella Vaccine/Titer or Feline FeLV Vaccine/Titer",
            section: "vaccines_titers",
            amountGold: 0,
            amountPlatinum: 20,
            icon: <ShotVaccineIcon className={preventiveIconSize} />,
            level: ["platinum"]
        },
        {
            text: "Microchip and/or Urinalysis",
            section: "tests_exams",
            amountGold: 30,
            amountPlatinum: 30,
            icon: <ClipboardMagnifyingGlassIcon className={preventiveIconSize} />,
            level: ["gold", "platinum"]
        }
    ];

    static PREVENTIVE_BENEFITS_PTZ_US: (params: PreventiveBenefitsParams) => PreventiveBenefit[] = ({
        petAge,
        quote,
        activePetIndex = 0,
        isApplyAllHidden = false,
        applyToAllPets = false,
        showRebrandIcon = false
    }) => {
        const policies = quote?.policies || [];
        const onlyCats = policies.length > 0 && policies.every(policy => policy.species === Species.Cat);
        const onlyDogs = policies.length > 0 && policies.every(policy => policy.species === Species.Dog);
        const activePet = policies[activePetIndex];
        const isActivePetCat = activePet?.species === Species.Cat;
        const isActivePetDog = activePet?.species === Species.Dog;
        const isPuppyOrKitten = petAge < 3;

        // Helper function to determine which text to show based on species
        const getSpeciesSpecificText = (key: keyof typeof Strings.PREVENTIVE_ITEMS): string => {
            if (onlyDogs) return Strings.PREVENTIVE_ITEMS[key]["DOG"];
            if (onlyCats) return Strings.PREVENTIVE_ITEMS[key]["CAT"];
            if (isApplyAllHidden || !applyToAllPets) {
                if (isActivePetDog) return Strings.PREVENTIVE_ITEMS[key]["DOG"];
                if (isActivePetCat) return Strings.PREVENTIVE_ITEMS[key]["CAT"];
            }
            return Strings.PREVENTIVE_ITEMS[key]["MIXED"];
        };

        return [
            {
                text: `Dental Cleaning`,
                amountGold: 100,
                amountPlatinum: 150,
                icon: showRebrandIcon ? <ToothBrushIconRebrand className={preventiveIconSize} /> : <ToothBrushIcon className={preventiveIconSize} />,
                level: ["gold"],
                section: "procedures"
            },
            {
                text: `${isPuppyOrKitten ? Strings.PTZ_US.BENEFITS_PREVENTIVE.SPAY_OR_DENTAL : Strings.PTZ_US.BENEFITS_PREVENTIVE.DENTAL_OR_SPAY}`,
                amountGold: 100,
                amountPlatinum: 150,
                icon: isPuppyOrKitten ? <SurgeryIcon className={preventiveIconSize} /> : <ToothBrushIcon className={preventiveIconSize} />,
                level: ["platinum"],
                section: "procedures"
            },
            {
                text: `Wellness Exam`,
                amountGold: 50,
                amountPlatinum: 50,
                icon: <CalendarHeartIcon className={preventiveIconSize} />,
                level: ["gold", "platinum"],
                section: "tests_exams"
            },
            {
                text: getSpeciesSpecificText("DHLPP"),
                amountGold: 20,
                amountPlatinum: 25,
                icon: <ShotVaccineIcon className={preventiveIconSize} />,
                level: ["gold", "platinum"],
                section: "vaccines_titers"
            },
            {
                text: getSpeciesSpecificText("TESTS"),
                amountGold: 20,
                amountPlatinum: 25,
                icon: <DiagnosticsIcon className={preventiveIconSize} />,
                level: ["gold", "platinum"],
                section: "tests_exams"
            },
            {
                text: getSpeciesSpecificText("RABIES"),
                amountGold: 20,
                amountPlatinum: 25,
                icon: <ShotVaccineIcon className={preventiveIconSize} />,
                level: ["gold", "platinum"],
                section: "vaccines_titers"
            },
            {
                text: `Deworming`,
                amountGold: 20,
                amountPlatinum: 25,
                icon: <TapewormIcon className={preventiveIconSize} />,
                level: ["gold", "platinum"],
                section: "routine_treatments"
            },
            {
                text: `Fecal Test`,
                amountGold: 20,
                amountPlatinum: 25,
                icon: <DiagnosticsIcon className={preventiveIconSize} />,
                level: ["gold", "platinum"],
                section: "tests_exams"
            },
            {
                text: `Flea/Heartworm Prevention`,
                amountGold: 0,
                amountPlatinum: 25,
                icon: <HealthCrossPawIcon className={preventiveIconSize} />,
                level: ["platinum"],
                section: "routine_treatments"
            },
            {
                text: `Health Certificate`,
                amountGold: 0,
                amountPlatinum: 25,
                icon: <CalendarHeartIcon className={preventiveIconSize} />,
                level: ["platinum"],
                section: "documentation"
            },
            {
                text: getSpeciesSpecificText("BORDETELLA"),
                amountGold: 0,
                amountPlatinum: 25,
                icon: <ShotVaccineIcon className={preventiveIconSize} />,
                level: ["platinum"],
                section: "vaccines_titers"
            },
            {
                text: `Urinalysis`,
                amountGold: 0,
                amountPlatinum: 25,
                icon: <ClipboardMagnifyingGlassIcon className={preventiveIconSize} />,
                level: ["platinum"],
                section: "tests_exams"
            },
            {
                text: `Blood Test`,
                amountGold: 0,
                amountPlatinum: 25,
                icon: <CancerIcon className={preventiveIconSize} />,
                level: ["platinum"],
                section: "tests_exams"
            }
        ];
    };

    static getPreventiveGoldBenefits(underwriter: PetUnderwriterType, params: PreventiveBenefitsParams): IconGridItem[] {
        switch (underwriter) {
            case "ptz-us":
                return IconUtils.PREVENTIVE_BENEFITS_PTZ_US(params)
                    .filter(benefit => benefit.level.includes("gold"))
                    .map(benefit => ({
                        text: benefit.text,
                        icon: benefit.icon,
                        amount: benefit.amountGold
                    }));

            case "ptz-ca":
                return IconUtils.PREVENTIVE_BENEFITS_PTZ_CA.filter(benefit => benefit.level.includes("gold")).map(benefit => ({
                    text: benefit.text,
                    icon: benefit.icon,
                    amount: benefit.amountGold
                }));

            default:
                return [];
        }
    }

    static getPreventivePlatinumBenefits(underwriter: PetUnderwriterType, params: PreventiveBenefitsParams): IconGridItem[] {
        switch (underwriter) {
            case "ptz-us":
                return IconUtils.PREVENTIVE_BENEFITS_PTZ_US(params)
                    .filter(benefit => benefit.level.includes("platinum"))
                    .map(benefit => ({
                        text: benefit.text,
                        icon: benefit.icon,
                        amount: benefit.amountPlatinum
                    }));

            case "ptz-ca":
                return IconUtils.PREVENTIVE_BENEFITS_PTZ_CA.filter(benefit => benefit.level.includes("platinum")).map(benefit => ({
                    text: benefit.text,
                    icon: benefit.icon,
                    amount: benefit.amountPlatinum
                }));

            default:
                return [];
        }
    }

    static hasBreedPreventiveRules(breed?: string) {
        if (!breed) return false;

        return !!BREED_PREVENTIVE_RULES[breed as keyof typeof BREED_PREVENTIVE_RULES];
    }

    static normalizeBreedName(breed?: string) {
        return breed?.trim();
    }

    static getBreedPreventiveMeta(pet?: Pet) {
        if (!pet) return undefined;

        const normalizedBreed = this.normalizeBreedName(pet.breedName);
        const breedMeta = normalizedBreed ? BREED_PREVENTIVE_METADATA[normalizedBreed as keyof typeof BREED_PREVENTIVE_METADATA] : undefined;

        if (!breedMeta) return undefined;

        const isPuppyOrKitten = (pet.age ?? 0) < 3;

        return isPuppyOrKitten ? breedMeta.puppy : breedMeta.adult;
    }

    static getPreventiveBenefitsBySection(
        underwriter: PetUnderwriterType,
        params: PreventiveBenefitsParams,
        level: "gold" | "platinum",
        showRebrandIcon = false
    ): SectionGridItem[] {
        let benefits: PreventiveBenefit[] = [];
        const petAge = params.petAge;
        const isPuppyOrKitten = petAge < 3;
        const policies = params.quote?.policies || [];
        const hasSinglePet = policies.length <= 1;
        const applyToAllPets = params.applyToAllPets ?? false;

        const activePet = policies[params.activePetIndex ?? 0];
        const breedName = activePet?.breedName;
        const isPreventiveVariant = params.applyBreedRecs === true;
        const shouldApplyBreedRules = isPreventiveVariant && level === "platinum";
        const breedRule = breedName ? BREED_PREVENTIVE_RULES[breedName as keyof typeof BREED_PREVENTIVE_RULES] : undefined;
        const ageRule = isPuppyOrKitten ? breedRule?.puppy : breedRule?.adult;

        const shouldUseScalpelIcon = underwriter === "ptz-us" && level === "platinum" && isPuppyOrKitten && (hasSinglePet || !applyToAllPets);
        // CA gold has no dental item, so its Procedures header shows the surgery icon instead of the
        // toothbrush. Icon only — the ptz-us scalpel flow also swaps the item text (showHighlight below).
        const useSurgeryProceduresIcon = shouldUseScalpelIcon || (underwriter === "ptz-ca" && level === "gold");

        if (underwriter === "ptz-us") {
            benefits = this.PREVENTIVE_BENEFITS_PTZ_US(params).filter(benefit => benefit.level.includes(level));
        }

        if (underwriter === "ptz-ca") {
            benefits = this.PREVENTIVE_BENEFITS_PTZ_CA.filter(benefit => benefit.level.includes(level));
        }

        const result: SectionGridItem[] = [];

        const benefitSections = getBenefitsSections({ showRebrandIcon: showRebrandIcon });
        const filteredSections = isPreventiveVariant ? benefitSections.filter(section => section.id !== "documentation") : benefitSections;

        filteredSections.forEach(baseSection => {
            const section =
                baseSection.id === "procedures" && useSurgeryProceduresIcon
                    ? {
                          ...baseSection,
                          icon: showRebrandIcon ? <SurgeryIconRebrand className={categorizedIconSize} /> : <SurgeryIcon className={categorizedIconSize} />
                      }
                    : baseSection;

            let sectionBenefits = benefits.filter(benefit => benefit.section === section.id);

            if (isPreventiveVariant && section.id === "tests_exams") {
                const healthCertificate = benefits.find(b => b.text === "Health Certificate");
                if (healthCertificate) {
                    sectionBenefits = [...sectionBenefits, healthCertificate];
                }
            }

            const showHighlight = shouldUseScalpelIcon && section.id === "procedures";

            if (sectionBenefits.length > 0) {
                result.push({
                    section,
                    items: sectionBenefits.map(benefit => {
                        let highlight = false;

                        const isSectionHighlighted = shouldApplyBreedRules && ageRule?.sections?.includes(section.id);
                        const benefitText = String(benefit.text);
                        const isItemHighlighted = shouldApplyBreedRules && ageRule?.items?.some((item: string) => benefitText.includes(item));

                        if (!isSectionHighlighted && isItemHighlighted) {
                            highlight = true;
                        }

                        return {
                            text: showHighlight ? (
                                <>
                                    <b>Spay/Neuter </b>
                                    <br className="hidden md:block" />
                                    or Dental Cleaning
                                </>
                            ) : (
                                benefit.text
                            ),
                            amount: level === "gold" ? benefit.amountGold : benefit.amountPlatinum,
                            highlight
                        };
                    }),
                    ...(showHighlight || (shouldApplyBreedRules && ageRule?.sections?.includes(section.id)) ? { highlight: true } : {})
                });
            }
        });

        return result;
    }
}
